import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import './index.less'
import { Button, Drawer, Form, Input, message, Radio, Space, Tooltip } from 'antd'
import { ExclamationCircleOutlined, LeftOutlined, SyncOutlined } from '@ant-design/icons'
import RowHeader from '../RowHeader'
import ExperEnv from '../ExperEnv'
import ExperPorts from '../ExperPorts'
import ExperEnvCenter from '../ExperEnvCenter'
import Flex from '../Flex'
import { CourseTreeCategory, CourseTreeType } from '@/utils/commonTypes'
import Base from './Base'
import { getExperEnvAll } from '@/api/modules/cli'
import { copyTemplateEnv, verifyResources } from '@/api/modules/community'
import { name } from 'pubsub-js'
import useResourcesLimit from '@/hooks/useResourcesLimit'

type DirectoryDrawerProps = {
    open: boolean
    onClose: () => void
    update?: boolean                   // 添加还是编辑  默认添加

    selected?: any                     // 当前课程选中层级的信息
    courseTree?: any[]                 // 课程目录信息  以下课程包括博客、快速体验，因为博客快速体验也是课程
    courseRepoEnvStatus?: any          // 课程环境信息

    labCode: string                    // 实验室code
    disabledUpdate?: boolean        // 是否禁用去升级
    userId: string                     // 用户id
    userName: string                   // 用户昵称

    onSave?: (data: any) => Promise<any> // 保存接口  保存成功会关闭窗口
}

// 步骤枚举类
// base 主页面   env 选择环境页面  port  添加端口
export enum StepEnum {
    BASE = 'BASE',
    ENV = 'ENV',
    PORT = 'PORT'
}
// 步骤类型
export type stepType = keyof typeof StepEnum

const DirectoryDrawer: React.FC<DirectoryDrawerProps> = (props) => {

    const {
        open,
        onClose,
        update = false,
        selected,
        courseTree,
        courseRepoEnvStatus,
        labCode,
        disabledUpdate,
        userId,
        userName,
        onSave
    } = props

    const [form] = Form.useForm()
    const [step, setStep] = useState<stepType>(StepEnum.BASE)                         // 当前更新步骤
    const [loading, setLoading] = useState(false)                                     // 保存loading
    const [envRefresh, setEnvRefresh] = useState(false)                               // 选择环境刷新按钮控制
    const [editType, setEditType] = useState<'stage' | 'chapter' | 'content' | null>(null)// 当前编辑的类型
    const [formData, setFormData] = useState<any>()                                   // 临时保存的数据
    const [experEnvList, setExperEnvList] = useState<any[]>([])                       // 环境列表
    const [initPort, setInitPort] = useState<any>()                                   // 初始化端口
    const hander = useRef<any>()
    const template = Form.useWatch('template', form)
    const handleResourseError = useResourcesLimit()

    // 初始化
    // 当前编辑或添加的类型或者还不确定（比如初始化没有目录的时候）
    // 当不确定的时候需要通过表单中的category来确定当前
    // 小节所有所有类型统一为content
    useEffect(() => {
        if (!open) {
            setStep(StepEnum.BASE)
            setFormData({})
            return
        }
        // 1 判断当前编辑的类型
        let _editType: 'stage' | 'chapter' | 'content' | null = null
        if (update) {
            _editType = [CourseTreeType.STAGE, CourseTreeType.CHAPTER].includes(selected?.describe?.type) ? selected?.describe?.type : 'content'
        } else if (courseTree?.length) {
            if (Object.keys(selected)?.length) {
                // 取下一级
                if (selected?.describe?.type === CourseTreeType.STAGE) {
                    _editType = 'chapter'
                } else if (selected?.describe?.type === CourseTreeType.CHAPTER) {
                    _editType = 'content'
                }
            } else {
                _editType = [CourseTreeType.STAGE, CourseTreeType.CHAPTER].includes(courseTree[0]?.describe?.type) ? courseTree[0]?.describe?.type : 'content'
            }
        }
        setEditType(_editType)

        // 判断当前设置的环境
        let now: any
        const hasSelected = Object.keys(selected).length !== 0
        if (!hasSelected && courseRepoEnvStatus?.template) {
            now = courseRepoEnvStatus?.template
        } else if (hasSelected && selected?.describe?.template) {
            now = selected?.describe?.template
        } else if (hasSelected && !selected?.describe?.template) {
            now = courseRepoEnvStatus?.template
        }

        // 2 设置初始的值
        const data = {
            category: _editType,
            title: update ? selected?.title : '',
            type: selected?.describe?.type,
            path: Object.keys(selected).length === 0 ? '/' : selected?.path,
            experEnv: now?.experEnv,
            expertools: now?.expertools,
            ports: now?.ports ?? [],
            duration: now?.duration ?? 30,
            name: now?.name
        }
        setFormData(data)
    }, [open, update, selected, courseTree, courseRepoEnvStatus])

    // 3 获取环境列表
    useEffect(() => {
        if (open) {
            fetchExperEnvList()
        }
    }, [open])


    // 计算标题
    const title = useMemo(() => {
        let title
        if (step === StepEnum.BASE) {
            title = update ? '编辑' : '添加'
            // 在章下新增小节或编辑小节标题后面才可能出现小节
            if ((update && selected?.describe?.category === CourseTreeCategory.CONTENT) || (!update && selected?.describe?.type === CourseTreeType.CHAPTER)) {
                title += '小节'
            }
        } else {
            title = step === StepEnum.ENV ? '选择环境' : `${initPort ? '编辑' : '添加'}端口`
        }
        return title!
    }, [step, update, selected, initPort])



    // 上一步或取消
    const prev = (cancel: boolean) => {
        if (cancel) {
            onClose()
            return
        }
        changeStep(StepEnum.BASE)
        setInitPort(null)
    }

    // 保存退出弹框  保存环境  保存端口设置
    const save = async () => {
        const data = await form.validateFields().catch(() => { })
        if (data == undefined) return
        setLoading(true)
        if (step === StepEnum.BASE) {
            await saveExit(data)
        } else if (step === StepEnum.ENV) {
            await saveEnv(data)
        } else if (step === StepEnum.PORT) {
            await savePort(data)
        }
        setLoading(false)
    }
    // 保存退出
    const saveExit = async (data: any) => {
        // 如何环境存在先校验环境时长，判断是否超出限制
        if (data?.experEnv) {
            const can = await verifyResources({
                labCode,
                data: { duration: data.duration }, quotaType: "CODE",
                quotaValue: "ENV_CONFIG_DURATION_LIMIT"
            }).catch((err) => {
                if (handleResourseError(err)) return
                message.destroy()
                message.error(err?.message ?? '校验失败')
            })
            if (can == undefined) return
        }
        // 环境是否需要先拷贝到实验室
        if (formData?.template?.location === data?.experEnv && formData?.template?.lab) {
            const res = await copyTemplateEnv({
                envId: formData?.template?.id,
                envLab: formData?.template?.lab,
                newLab: labCode,
            }).catch((err) => {
                if (handleResourseError(err)) return
                message.destroy()
                message.error(err?.message ?? '保存失败')
            })
            if (res == undefined) return
            data.experEnv = res?.location
        }
        const _data = {
            title: data.title.trim(),
            path: update ? data.path : '',
            parent: update ? selected.parent : data.path,
            describe: selected?.describe ? JSON.parse(JSON.stringify(selected?.describe)) : {},
        }
        if (!update) {
            if (!selected?.describe) {
                const courseList = courseTree?.filter((it: any) => it?.parent === '')
                _data.describe.index = courseList?.length ?? 0
            } else {
                _data.describe.index = selected?.children?.length || 0
            }
            _data.describe.category = (data.category && data.category !== 'content') ? 'chapter' : 'content'
            _data.describe.type = (data.category && data.category !== 'content') ? data.category : data.type

            const type: any = {
                "stage": '阶段',
                "chapter": '章',
                "text": '文档',
                "notebook": 'Notebook',
                "video": '视频',
                "exam": '考试',
            }
            _data.describe.title = type[data.type]
        }

        // 特殊情况   课程没有类型(创建课程自动创建的目录没有类型)
        if (selected && !_data?.describe?.type) {
            _data.describe.type = data.type
        }

        const ports = data?.ports?.map((item: any) => {
            const type = item?.type ?? 'system'
            return { ...item, type }
        })
        const template = {
            ..._data?.describe?.template,
            experEnv: data?.experEnv,
            expertools: data?.expertools,
            ports,
            duration: data?.duration,
            name: data?.name,
        }
        _data.describe.template = template
        onSave?.(_data)
    }
    // 保存环境
    const saveEnv = async (data: any) => {
        const { template } = data
        if (template && template?.id !== 'default-template-id') {
            setExperEnvList([...experEnvList, template])
            setFormData({
                ...formData,
                template,
                experEnv: template?.location,
                expertools: template?.containers?.[0]?.name,
                ports: template?.containers?.[0]?.ports?.map((item: any) => ({ ...item, choice: true })) ?? [],
                name: template?.name,
            })
        } else {
            setFormData({
                ...formData,
                template,
                experEnv: undefined,
                expertools: undefined,
                ports: [],
                name: undefined,
            })
        }
        changeStep(StepEnum.BASE)
    }
    // 保存端口
    const savePort = async (data: any) => {
        data.port = Number(data?.port)
        const port = data?.port
        if (formData?.ports?.some((item: any) => item?.port !== initPort?.port && item?.port === port)) {
            message.destroy()
            message.warning('端口号已存在，请重新输入')
            return
        }
        if (initPort) {
            const res = { ...initPort, ...data }
            const ports = formData?.ports?.map((item: any) => {
                if (item?.port === initPort?.port) {
                    return { ...item, ...res }
                }
                return { ...item }
            })
            setFormData({ ...formData, ports })
        } else {
            const port = {
                ...data,
                choice: true,
                type: 'creator'
            }
            const ports = formData?.ports?.concat([port]) ?? [port]
            setFormData({ ...formData, ports })
        }
        changeStep(StepEnum.BASE)
        setInitPort(null)
    }

    // 改变当前步骤
    const changeStep = (next: StepEnum) => {
        // 跳转到其他步骤优先保存
        if (step === StepEnum.BASE) {
            const data = form.getFieldsValue()
            console.log(data, formData);
            
            setFormData({ ...formData, ...data })
        }
        setStep(next)
    }

    // 获取环境列表
    const fetchExperEnvList = async () => {
        const res = await getExperEnvAll({ owner: userId, lab: labCode }).catch(() => { })
        setExperEnvList(res ?? [])
    }

    // 更新端口
    const updatePort = (type: 'update' | 'add', ports: any, value?: any) => {
        if (type === 'update') {
            setInitPort(value)
        }
        changeStep(StepEnum.PORT)
    }



    return (
        <Drawer
            placement='right'
            closable={false}
            destroyOnClose
            onClose={onClose}
            open={open}
            width={712}
            maskClosable={false}
            rootClassName='DirectoryDrawer'
        >
            <Flex justify='space-between' align='center' style={{ height: '48px', borderBottom: '1px solid #E5E5E5', padding: '0 30px' }}>
                <span>
                    {
                        step !== StepEnum.BASE && <LeftOutlined onClick={() => { changeStep(StepEnum.BASE) }} style={{ color: '#999999', fontSize: '16px' }} />
                    }
                </span>
                <span style={{ color: '#333333', fontSize: '16px', fontWeight: 'bold' }}>{title}</span>
                <span>
                    {
                        step === StepEnum.ENV && <Tooltip
                            placement="bottomLeft"
                            title={'刷新实验环境'}
                            arrow={false}
                            color='white'
                            overlayClassName='custom-tooltip'
                        >
                            <SyncOutlined className='DirectoryDrawer-refresh' spin={envRefresh} style={{ color: '#999999', fontSize: '16px' }} onClick={async () => {
                                setEnvRefresh(true)
                                await hander.current?.reFresh().finally(() => {
                                    setEnvRefresh(false)
                                    message.success('刷新成功')
                                })
                            }} />
                        </Tooltip>
                    }
                </span>
            </Flex>

            <div className='DirectoryDrawer-content'>
                <Form
                    form={form}
                    colon={false}
                    autoComplete="off"
                    style={{ height: '100%' }}
                >
                    {
                        step === StepEnum.BASE ? <Base disabledUpdate={disabledUpdate} labCode={labCode} changeStep={changeStep} editType={editType} update={update} formData={formData} experEnvList={experEnvList} updatePort={updatePort} selected={selected} /> :
                            step === StepEnum.ENV ? <ExperEnvCenter ref={hander} labCode={labCode} initTemplate={formData?.experEnv} /> :
                                step === StepEnum.PORT ? <ExperPorts initValue={initPort} /> :
                                    <></>
                    }
                </Form>
            </div>

            <Flex gap={20} justify='center' align='center' style={{ height: '72px' }}>
                <Button onClick={() => { prev(step === StepEnum.BASE) }}>{step === StepEnum.BASE ? '取消' : '上一步'}</Button>
                <Button disabled={step === StepEnum.ENV && !template} loading={loading} type='primary' onClick={save}>保存</Button>
            </Flex>
        </Drawer>
    )
}

export default memo(DirectoryDrawer)
